import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ExclamationSpeechIcon } from '@domgen/base-design-system-2.0/icons/ExclamationSpeech';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { getSystemCode } from '../utils/getSystemCode';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { useNavigate } from 'react-router-dom';
import { capturePostHogEvent } from '../services/posthog/capture';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useConfirmation } from '../store/selectors/useConfirmation';
import { useGaEventData } from '../store/selectors/useGaEventData';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { store } from '../store/store';
import { formApplianceName } from '../utils/store/formApplianceName';
import { formCategoryName } from '../utils/store/formCategoryName';
import { StoreEvent } from '../enums/storeEvent';
import { useStoreContext } from '../store/selectors/useStoreContext';

export function ErrorPage() {
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const formData = useApplianceFormData();
  const confirmation = useConfirmation();
  const gaEventData = useGaEventData();
  const posthog = usePostHog();
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();
  const navigate = useNavigate();
  const storeContext = useStoreContext();

  useEffect(() => {
    gaPageView({
      pagename: 'olr-generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided', // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });
  }, []);

  const handlePhoneNumber = () => {
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Generic Error page',
      eventLabel: 'Phone Number clicked',
      pagename: 'olr-generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided', // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });

    capturePostHogEvent(posthog, 'error-phone-clicked', storeContext);
  };

  const handleTryAgain = () => {
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Generic Error page',
      eventLabel: 'Try Again button clicked',
      pagename: 'olr-generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided',
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });

    capturePostHogEvent(posthog, 'error-try-again-clicked', storeContext);
    store.send({ type: StoreEvent.INITIALISE });
    navigate('/', { replace: true });
  };

  return (
    <div className="flex flex-col justify-center items-center text-center px-4 my-8 gap-space-500">
      <span className="text-(--color-primary-500)">
        <ExclamationSpeechIcon />
      </span>
      <h1 className="text-h1 text-center text-(--color-text-headline)">
        Sorry, something's not right.
      </h1>
      <p className="font-family-body">
        It looks like there's been a technical issue but we're working to fix it.
      </p>
      <div className="flex flex-col gap-2 items-center">
        <p className="font-family-body">
          <strong>Please try again later or call us to complete your registration:</strong>
        </p>
        <TextLink onClick={handlePhoneNumber} href="tel:+18553213007">
          +1-855-321-3007
        </TextLink>
      </div>
      <div className="w-full tablet:max-w-90  [&_button]:leading-5">
        <Button variant="primary" onClick={handleTryAgain}>
          Try Again
        </Button>
      </div>
    </div>
  );
}
